import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { PinDialog } from "@/components/dialogs/PinDialog";
import { ManagerPinContext } from "@/contexts/ManagerPinContext";
import { useVerifyPin } from "@/hooks/useStaff";
import { useStoreMe } from "@/hooks/useStoreMe";
import { pinSession } from "@/utils/pinSession";

// bundle-barrel-imports: Direct imports instead of barrel

/** Interval kiểm tra PIN session hết hạn (10 giây) */
const SESSION_CHECK_INTERVAL_MS = 10_000;

export const ProtectedManagerRoute = () => {
  // Dùng useStoreMe để kiểm tra auth thay vì getCookie (HttpOnly)
  const { data: storeData } = useStoreMe();
  const isAuthenticated = !!storeData;
  const navigate = useNavigate();
  const location = useLocation();

  // Manager PIN state — khởi tạo từ pinSession nếu còn hạn
  const [managerPin, setManagerPin] = useState<string | null>(() =>
    pinSession.getPin(),
  );
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: verifyPin, isPending: isLoading } = useVerifyPin();

  // ============================================
  // Interval check: tự động clear PIN khi session hết hạn
  // ============================================
  useEffect(() => {
    if (!managerPin) return;

    const interval = setInterval(() => {
      if (!pinSession.isValid()) {
        setManagerPin(null);
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [managerPin]);

  // ============================================
  // Subscribe vào pinSession để nhận thông báo clear từ bên ngoài
  // ============================================
  useEffect(() => {
    const unsubscribe = pinSession.subscribe(() => {
      // Khi session bị clear (hết hạn) → cập nhật state
      if (!pinSession.isValid()) {
        setManagerPin(null);
      }
    });

    return unsubscribe;
  }, []);

  const handleSubmit = useCallback(
    async (fullPin: string) => {
      setError(null);

      try {
        const data = await verifyPin({ pin: fullPin });

        if (data.role !== "MANAGER") {
          setError(t("staff.manager_only"));
          return;
        }

        // Lưu PIN vào session (module-level) và state
        pinSession.start(fullPin);
        setManagerPin(fullPin);
      } catch (err: unknown) {
        const error = err as {
          response?: { status: number; data?: { message: string } };
          message?: string;
        };

        if (
          error.response?.status === 404 &&
          error.response?.data?.message === "Invalid PIN"
        ) {
          setError(t("staff.invalid_manager_pin"));
        } else {
          setError(error.message || t("auth.errors.default"));
        }
      }
    },
    [verifyPin],
  );

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        navigate("/pos");
      }
    },
    [navigate],
  );

  const titleName = location.pathname.includes("dashboard")
    ? "Dashboard"
    : "Transactions";

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!managerPin) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <PinDialog
          open={true}
          onOpenChange={handleClose}
          onSubmit={handleSubmit}
          titleName={titleName}
          error={error}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <ManagerPinContext.Provider value={{ managerPin }}>
      <Outlet />
    </ManagerPinContext.Provider>
  );
};
